import { get, isEmpty } from 'lodash';
import { getReferralCode } from './localStorage';
import { getRHSourceCampaign } from './sessionStorage';

export const CURRENT_CAMPAIGNS = {
  staging: {
    'athletic-trainer': 'MF3fd783141f',
    'event-operator': 'MFc6b1193586',
  },
  production: {
    'athletic-trainer': 'MF8982b8b773',
    'event-operator': 'MF41de858d1a',
  },
};

/**
 * Get ReferralHero campaigns for the current environment
 * @returns {object} An object with RH campaigns Ids based on the environment (e.g., { event-operator: 'MFc6b1193586', athletic-trainer: 'MF3fd783141f' })
 */
export const getActiveEnvironmentCampaigns = () => {
  const env = process.env.NODE_ENV === 'production' ? 'production' : 'staging';

  return CURRENT_CAMPAIGNS[env];
};

/**
 * Find the current active ReferralHero campaign
 * Prioritizes campaigns that have a referrer set
 *
 * @param {string[]} campaigns - Array of campaign keys
 * @returns {string|undefined} The current campaign key or undefined
 */
export const RHCurrentCampaign = campaigns => {
  return campaigns.find(c => {
    const campaign = get(window, `RH_${c}`, {});
    return !isEmpty(campaign.referrer);
  });
};

export const RHCurrentCampaignByMwr = (campaigns, mwrCode) => {
  return campaigns.find(c => {
    const campaign = get(window, `RH_${c}`, {});
    return !isEmpty(campaign.referrer) && campaign.referrer === mwrCode;
  });
};

/**
 * Find the ReferralHero campaign that matches a specific referral code
 *
 * @param {string} mwrCode - The mwr/referral code to match
 * @returns {Object|null} The matching campaign object or null
 */
export const findCampaignByMwr = mwrCode => {
  if (!mwrCode) return null;

  const source = getRHSourceCampaign();
  const campaigns = getActiveEnvironmentCampaigns();
  const campaign = campaigns[source];
  const campaignMatchMwrCode = get(window, `RH_${campaign}`, {}).referrer === mwrCode;

  if (campaignMatchMwrCode) return get(window, `RH_${campaign}`);

  // fallback if the source was corrupted
  return RHCurrentCampaignByMwr(Object.values(campaigns), mwrCode);
};

/**
 * Get the referral code with fallback strategy:
 * 1. Try localStorage (most reliable - captured from URL)
 * 2. Try ReferralHero window object (fallback for existing campaigns)
 *
 * @returns {string|null} The referral code or null
 */
export const getReferralCodeWithFallback = (location = {}) => {
  // First, try get mwr params from url (most reliable)
  if (!isEmpty(location)) {
    const params = new URLSearchParams(get(location, 'search', ''));

    if (params.get('mwr')) {
      return params.get('mwr');
    }
  }

  // Second, try localStorage
  const storedCode = getReferralCode();
  if (storedCode) {
    return storedCode;
  }

  // Fallback: Try to get from ReferralHero window object
  const campaigns = getActiveEnvironmentCampaigns();
  const currentCampaign = RHCurrentCampaign(Object.values(campaigns));

  if (currentCampaign) {
    const RHCampaign = get(window, `RH_${currentCampaign}`, null);
    return RHCampaign ? get(RHCampaign, 'referrer', null) : null;
  }

  return null;
};
